import clsx from 'clsx';
import isEmpty from 'is-empty';
import type * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import { Badge, BadgeIconWrapper, IntlPluralize, ListItem, Modal } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { useSpaceContext } from '../context';

/**
 * Modal to confirm the keeping of page and its parent and children.
 */
export function ConflictsKeepPageModal(props: {
    page: hiveCore.RevisionPage;
    parentPages: hiveCore.RevisionPage[];
    childPages: hiveCore.RevisionPage[];
    revision: hiveCore.Revision;
    onClose: () => void;
}): React.ReactElement {
    const { parentPages, childPages, onClose, revision, page } = props;
    const [loading, wrapLoading] = useLoadingState();
    const hiveBranch = useHiveBranch();
    const { analyticsContext } = useSpaceContext();

    const onComplete = async (multiple = true) => {
        assertIsDefined(hiveBranch);

        await wrapLoading(async () => {
            await hiveBranch.change((change) => {
                [page, ...(multiple ? parentPages : []), ...(multiple ? childPages : [])].forEach(
                    (pageItem) => {
                        change.resolveConflict(pageItem.id);
                    }
                );
            });

            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_conflicts_keep_page',
                pageKind: page.type,
            });
        });

        onClose();
    };

    return (
        <Modal
            title="Keep page"
            onClose={onClose}
            footer={
                <div className="flex justify-end gap-2">
                    <Button kind="secondary" onPress={onClose} disabled={loading} label="Cancel" />
                    <Button
                        kind="secondary"
                        onPress={async () => onComplete(false)}
                        label="Keep only this page"
                    />
                    <Button kind="merge" onPress={async () => onComplete()} label="Keep" />
                </div>
            }
        >
            {!isEmpty(parentPages) && (
                <div className={clsx('flex', !isEmpty(childPages) && 'mb-6 border-b pb-6')}>
                    <ConflictParentPages
                        revision={revision}
                        pages={parentPages}
                        onClose={onClose}
                    />
                </div>
            )}

            <ConflictChildPages revision={revision} pages={childPages} onClose={onClose} />
        </Modal>
    );
}

function ConflictParentPages(props: {
    revision: hiveCore.Revision;
    pages: hiveCore.RevisionPage[];
    onClose: () => void;
}) {
    const { pages, revision } = props;
    const { conflicts } = revision;
    const count = pages.length;

    return pages.length ? (
        <div className="mt-4 flex flex-col">
            <div className="mb-2 flex">
                <Badge kind="warning" icon={Icon.Warning} />
                <div className="ml-4 text-ui-action">
                    There {count > 1 ? 'are' : 'is'} {pages.length} conflicted parent{' '}
                    <IntlPluralize count={count} singular="page" plural="pages" />. Keeping the
                    current page, you would keep{' '}
                    <IntlPluralize count={count} singular="it" plural="all of them" />
                </div>
            </div>
            <div className="flex">
                {pages.map((page) => {
                    const conflictType = conflicts?.pages[page.id];
                    return (
                        <ListItem
                            key={page.id}
                            layout="wide"
                            icon={
                                <BadgeIconWrapper>
                                    {conflictType ===
                                        hiveCore.RevisionConflictState
                                            .DeletedInBaseSubtreeModifiedInHead ||
                                    conflictType ===
                                        hiveCore.RevisionConflictState
                                            .DeletedInHeadSubtreeModifiedInBase ? (
                                        <Badge kind="warning" icon={Icon.Conflict} />
                                    ) : (
                                        <Badge kind="danger" icon={Icon.Trashcan} />
                                    )}
                                </BadgeIconWrapper>
                            }
                            title={page.title}
                        />
                    );
                })}
            </div>
        </div>
    ) : null;
}

function ConflictChildPages(props: {
    revision: hiveCore.Revision;
    pages: hiveCore.RevisionPage[];
    onClose: () => void;
}) {
    const { pages, revision } = props;
    const { conflicts } = revision;

    const count = pages.length;

    return pages.length ? (
        <div className="mt-4 flex flex-col">
            <div className="mb-2 flex">
                <Badge kind="warning" icon={Icon.Warning} />
                <div className="ml-4 text-ui-action">
                    There {pages.length > 1 ? 'are' : 'is'} {pages.length} nested{' '}
                    <IntlPluralize count={count} singular="page" plural="pages" />. Keeping the
                    current page, you would keep{' '}
                    <IntlPluralize count={count} singular="it" plural="all of them" />
                </div>
            </div>
            <div className="flex">
                {pages.map((page) => {
                    const conflictType = conflicts?.pages[page.id];
                    return (
                        <ListItem
                            key={page.id}
                            layout="wide"
                            icon={
                                <BadgeIconWrapper>
                                    {conflictType ===
                                        hiveCore.RevisionConflictState
                                            .DeletedInBaseSubtreeModifiedInHead ||
                                    conflictType ===
                                        hiveCore.RevisionConflictState
                                            .DeletedInHeadSubtreeModifiedInBase ? (
                                        <Badge kind="warning" icon={Icon.Conflict} />
                                    ) : (
                                        <Badge kind="danger" icon={Icon.Trashcan} />
                                    )}
                                </BadgeIconWrapper>
                            }
                            title={page.title}
                        />
                    );
                })}
            </div>
        </div>
    ) : null;
}
